/**
 * overlapLayout.js
 * Side-by-side lane assignment algorithm for overlapping events.
 *
 * When multiple events overlap in a staff column, this assigns each event
 * a "lane" so they render side-by-side instead of stacked on top of each other.
 */

const MAX_VISIBLE_LANES = 3;

/**
 * Compute overlap lanes for a list of events in a single column.
 *
 * @param {DiaryEvent[]} events - All events for one staff/date
 * @returns {OverlapEvent[]} - Events with lane + laneCount assigned
 *
 * Each returned event gets:
 *   .lane      - 0-based lane index
 *   .laneCount - total lanes in this overlap cluster
 *   .overflow  - number of hidden events if this is the last visible lane
 */
export function computeOverlapLanes(events) {
  if (!events || events.length === 0) return [];

  // Filter out background-type events (non-working overlays) from overlap calculation
  const layoutEvents = events.filter(e => e.type !== 'background');
  const passThrough = events.filter(e => e.type === 'background').map(e => ({
    ...e,
    lane: 0,
    laneCount: 1,
    overflow: 0,
  }));

  if (layoutEvents.length === 0) return passThrough;

  // Sort by start time, then longest duration first (so wider events get earlier lanes)
  const sorted = [...layoutEvents].sort((a, b) => {
    if (a.startMinutes !== b.startMinutes) return a.startMinutes - b.startMinutes;
    return (b.endMinutes - b.startMinutes) - (a.endMinutes - a.startMinutes);
  });

  // Step 1: Find overlap clusters (groups of events that all overlap with each other)
  const clusters = [];
  let currentCluster = [sorted[0]];
  let clusterEnd = sorted[0].endMinutes;

  for (let i = 1; i < sorted.length; i++) {
    const evt = sorted[i];
    if (evt.startMinutes < clusterEnd) {
      // Overlaps with current cluster
      currentCluster.push(evt);
      if (evt.endMinutes > clusterEnd) clusterEnd = evt.endMinutes;
    } else {
      // New cluster
      clusters.push(currentCluster);
      currentCluster = [evt];
      clusterEnd = evt.endMinutes;
    }
  }
  clusters.push(currentCluster);

  // Step 2: Assign lanes within each cluster
  const result = [];

  clusters.forEach(cluster => {
    if (cluster.length === 1) {
      result.push({ ...cluster[0], lane: 0, laneCount: 1, overflow: 0 });
      return;
    }

    // Greedy lane assignment
    const lanes = []; // lanes[i] = endMinutes of the event occupying lane i

    const assigned = cluster.map(evt => {
      // Find the first lane where this event can fit (no overlap)
      let assignedLane = -1;
      for (let l = 0; l < lanes.length; l++) {
        if (lanes[l] <= evt.startMinutes) {
          assignedLane = l;
          break;
        }
      }
      if (assignedLane === -1) {
        assignedLane = lanes.length;
      }
      lanes[assignedLane] = evt.endMinutes;
      return { ...evt, lane: assignedLane };
    });

    const totalLanes = lanes.length;

    // Apply the lane count and handle overflow
    assigned.forEach(evt => {
      if (totalLanes <= MAX_VISIBLE_LANES) {
        result.push({ ...evt, laneCount: totalLanes, overflow: 0 });
      } else {
        // Cap at MAX_VISIBLE_LANES
        if (evt.lane < MAX_VISIBLE_LANES - 1) {
          result.push({ ...evt, laneCount: MAX_VISIBLE_LANES, overflow: 0 });
        } else if (evt.lane === MAX_VISIBLE_LANES - 1) {
          // Last visible lane shows "+N" overflow
          const hiddenCount = totalLanes - MAX_VISIBLE_LANES;
          result.push({ ...evt, laneCount: MAX_VISIBLE_LANES, overflow: hiddenCount });
        } else {
          // Hidden event (lane >= MAX_VISIBLE_LANES), still include but mark hidden
          result.push({ ...evt, laneCount: totalLanes, overflow: 0, hidden: true });
        }
      }
    });
  });

  return [...result, ...passThrough];
}

export { MAX_VISIBLE_LANES };
